import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Grid, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';

import Container from 'components/Container';
import Main from 'layouts/Main';
import CategoryTile from 'components/CategoryTile';
import SEO from 'components/SEO';
import InlineError from 'components/InlineError';

import httpClient from 'API/httpClient';
import { GroupTile, GroupBreadcrumb } from 'types/group';
import { ApiError } from 'types/apiError';

const CataloguePage = () => {
  const [categories, setCategories] = useState<GroupTile[]>([]);
  const [breadcrumbsData, setBreadcrumbsData] = useState<GroupBreadcrumb[]>([]);
  const [error, setError] = useState<ApiError | null>(null);
  const theme = useTheme();
  const params = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const fetchCategories = async (idsString?: string) => {
    const endpoint = idsString ? `/tiles/${idsString}` : '/tiles';
    setError(null);
    try {
      const response = await httpClient.get<GroupTile[]>(endpoint);
      const data = response.data;
      setCategories(data);

      // Fetch breadcrumbs if we have categories
      if (data.length > 0 && data[0].id) {
        try {
          const breadcrumbsResponse = await httpClient.get(
            `/group/${data[0].id}/breadcrumbs`,
          );
          setBreadcrumbsData(breadcrumbsResponse.data.breadcrumbs);
        } catch {
          // Best-effort: category tiles still render without breadcrumbs.
        }
      }
    } catch (err) {
      setError(err as ApiError);
      setCategories([]);
    }
  };

  useEffect(() => {
    fetchCategories(params.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.id]);

  const handleNavigate = async (
    e: React.MouseEvent,
    categoryId: string | number,
  ) => {
    e.preventDefault();

    if (categoryId === 'products') {
      navigate('/products');
      return;
    }

    try {
      // Fetch the category to get its children
      const response = await httpClient.get<GroupTile[]>(
        `/tiles/${categoryId}`,
      );
      const category = response.data;
      if (category && category.length > 0) {
        // Get all children IDs
        const childrenIds = category[0].Children?.map((child) => child.id).join(
          ',',
        );
        if (childrenIds) {
          navigate(`/products/${childrenIds}`);
        }
      }
    } catch {
      // Best-effort: clicking the breadcrumb just won't navigate.
    }
  };

  const renderCategories = () => {
    if (categories.length === 0) {
      return null;
    }
    return categories.map((category) => (
      <CategoryTile key={category.id} category={category} />
    ));
  };

  return (
    <Main>
      <SEO
        title={t('seo.products.title')}
        description={t('seo.products.description')}
        keywords={t('seo.products.keywords')}
        url="/products"
      />
      <Box>
        <Container>
          <Box marginBottom={2}>
            <Breadcrumbs
              sx={{
                '& .MuiBreadcrumbs-ol': {
                  display: 'flex',
                  alignItems: 'center',
                },
                padding: '10px 0',
              }}
            >
              <Link
                underline="hover"
                color="inherit"
                href={`/products`}
                onClick={(e) => handleNavigate(e, 'products')}
                sx={{
                  cursor: 'pointer',
                  '&:hover': {
                    color: theme.palette.primary.main,
                  },
                }}
              >
                {'Products'}
              </Link>
              {breadcrumbsData.map((item) => (
                <Link
                  key={item.id}
                  underline="hover"
                  color="inherit"
                  href={`/products/${item.id}`}
                  onClick={(e) => handleNavigate(e, item.id)}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': {
                      color: theme.palette.primary.main,
                    },
                  }}
                >
                  {item.name}
                </Link>
              ))}
            </Breadcrumbs>
          </Box>
          {error && categories.length === 0 ? (
            <InlineError onRetry={() => fetchCategories(params.id)} />
          ) : (
            <Grid container spacing={4}>
              {renderCategories()}
            </Grid>
          )}
        </Container>
      </Box>
    </Main>
  );
};

export default CataloguePage;
